import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  FolderArchive,
  ShieldCheck,
} from "lucide-react";
import { WorkspaceHeader } from "@shared/components/WorkspaceHeader";
import { IsoCertificateActions } from "../shared/certificados-iso/components/IsoCertificateActions";
import { IsoCertificatePreviewModal } from "../shared/certificados-iso/components/IsoCertificatePreviewModal";
import { useIsoCertificates } from "../shared/certificados-iso/hooks/useIsoCertificates";
import type {
  IsoCertificateSlot,
  IsoCertificateStandard,
} from "../shared/certificados-iso/types/certificados-iso";
import "../styles/certificaciones.css";

type StandardCard = {
  title: string;
  subtitle: string;
  description: string;
  status: string;
  standard: IsoCertificateStandard;
  to:
    | "/certificaciones/iso-9001-14001"
    | "/certificaciones/iso-27001-27701";
  icon: typeof CheckCircle2;
};

const standards: StandardCard[] = [
  {
    title: "CALIDAD y MEDIOAMBIENTE (ISO 9001-14001)",
    subtitle: "Sistema actual",
    description:
      "Agrupa las secciones existentes de Calidad: DAFO, incidencias, objetivos, requisitos legales, riesgos y oportunidades, planificación de cambios y suministradores homologados.",
    status: "Activo",
    standard: "iso-9001-14001",
    to: "/certificaciones/iso-9001-14001",
    icon: FileCheck2,
  },
  {
    title: "PRIVACIDAD y SEGURIDAD (ISO 27001-27701)",
    subtitle: "Sistema actual",
    description:
      "Incluye las secciones SOA, Plan Anexo A y Documentos del sistema para la gestión del SGSI.",
    status: "Activo",
    standard: "iso-27001-27701",
    to: "/certificaciones/iso-27001-27701",
    icon: ShieldCheck,
  },
];

export function CertificacionesPage() {
  const navigate = useNavigate();
  const certificates = useIsoCertificates();
  const [previewSlot, setPreviewSlot] = useState<IsoCertificateSlot | null>(null);

  return (
    <div className="page-shell certificaciones-page-shell">
      <WorkspaceHeader
        eyebrow="Sistema de certificaciones"
        title="Certificaciones"
        description="Centraliza los sistemas de gestión certificados de la organización, sus secciones operativas, registros y documentación para facilitar el seguimiento, el control y las auditorías."
        icon={CheckCircle2}
        metrics={[
          {
            label: "Estándares",
            value: 2,
            icon: FolderArchive,
          },
        ]}
      />

      <section className="certificaciones-content-group">
        <div className="certificaciones-group-heading">
          <span>Normas ISO</span>
          <strong>2 estándares</strong>
        </div>

        {certificates.error ? (
          <div className="certificaciones-certificates-error" role="alert">
            {certificates.error}
          </div>
        ) : null}

        <div className="certificaciones-standards-grid">
          {standards.map((standard) => {
            const Icon = standard.icon;

            return (
              <article key={standard.title} className="certificaciones-standard-card">
                <button
                  type="button"
                  className="certificaciones-standard-main"
                  onClick={() => void navigate({ to: standard.to })}
                >
                  <div className="certificaciones-standard-card-header">
                    <span className="certificaciones-standard-icon">
                      <Icon size={24} />
                    </span>
                    <span className="certificaciones-standard-status">
                      {standard.status}
                    </span>
                  </div>

                  <div className="certificaciones-standard-body">
                    <p className="certificaciones-standard-eyebrow">
                      {standard.subtitle}
                    </p>
                    <h2>{standard.title}</h2>
                    <p>{standard.description}</p>
                  </div>

                  <span className="certificaciones-standard-action">
                    Abrir estándar
                    <ArrowRight size={16} />
                  </span>
                </button>

                <IsoCertificateActions
                  standard={standard.standard}
                  slots={certificates.slots}
                  loading={certificates.loading}
                  variant="card"
                  onPreview={setPreviewSlot}
                />
              </article>
            );
          })}
        </div>
      </section>

      <IsoCertificatePreviewModal
        slot={previewSlot}
        onClose={() => setPreviewSlot(null)}
      />
    </div>
  );
}
